import "./style.scss";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { getTeachers } from "../../store/middleware";
import Filter from "../../components/elements/Filter";
import Header from "../../components/Header/NewHeader";
import SideMenu from "../../components/SideMenu/SideMenu";
import TableList from "../../components/elements/TableList";
import { decryptIfEncrypted } from "../../utils/fieldDecryptor";
import { setDetailTeacher } from "../../store/slices/companyManager.slice";
import teacherIcon from "../../assets/images/icons/icon_teacher_header.svg";
import { getCountries, getGlobalLanguages } from "../../store/middleware/generic.middleware";

function Teachers() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teacherManagement } = useSelector((state) => state.companyManagerReducer);

  const [countries, setCountries] = useState([]);
  const [globalLanguages, setGlobalLanguages] = useState([]);
  const [filter, setFilter] = useState({
    name: "",
    nativeLanguage: "",
    otherLanguage: "",
    country: "",
    firstClass: "",
    demoClass: "",
    ratingFrom: "",
    ratingTo: "",
    status: "",
    isTraining: "",
  });

  const isTrainingOptions = ["Yes", "No"]?.map((option, i) => ({ id: i, name: option }));
  const statusOptions = ["Inactive", "Active"]?.map((option, i) => ({ id: i, name: option }));
  const rangeOptions = ["0", "0.5", "1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5"]?.map((option) => {
    return { id: option, name: option };
  });

  const filters = [
    { name: "name", value: filter.name, label: "Name", placeholder: t("TEACHER_NAME"), type: "text" },
    {
      type: "select",
      placeholder: "Select",
      name: "nativeLanguage",
      label: t("NATIVE_LANGUAGE"),
      options: globalLanguages,
      value: filter.nativeLanguage,
    },
    {
      type: "select",
      name: "otherLanguage",
      placeholder: "Select",
      label: t("OTHER_LANGUAGE"),
      value: filter.otherLanguage,
      options: globalLanguages,
    },
    {
      type: "select",
      name: "country",
      label: t("COUNTRY"),
      value: filter.country,
      placeholder: "Select",
      options: countries,
    },

    {
      type: "select",
      name: "firstClass",
      label: t("FIRST_CLASS"),
      placeholder: "Select",
      options: statusOptions,
      value: filter.firstClass,
    },
    {
      type: "select",
      name: "demoClass",
      label: t("DEMO_CLASS"),
      placeholder: "Select",
      options: statusOptions,
      value: filter.demoClass,
    },
    {
      type: "select",
      name: "ratingFrom",
      label: "Rating From",
      placeholder: "Select",
      options: rangeOptions,
      value: filter.ratingFrom,
    },
    {
      type: "select",
      name: "ratingTo",
      label: "Rating To",
      placeholder: "Select",
      options: rangeOptions,
      value: filter.ratingTo,
    },
    {
      type: "select",
      name: "isTraining",
      label: t("TRAINING"),
      value: filter?.isTraining,
      placeholder: "Select",
      options: isTrainingOptions,
    },
    {
      type: "select",
      name: "status",
      label: t("STATUS"),
      value: filter.status,
      placeholder: "Select",
      options: statusOptions,
    },
  ];

  const listItems = [
    { label: t("NAME"), name: "name" },
    { label: t("NATIVE_LANGUAGE"), name: "nativeLanguage" },
    { label: t("OTHER_LANGUAGE"), name: "otherLanguage" },
    { label: t("COUNTRY"), name: "country" },
    { label: t("FIRST_CLASS"), name: "firstClass" },
    { label: t("DEMO_CLASS"), name: "demoClass" },
    { label: t("TRAINING"), name: "isTraining" },
    { label: t("RATING"), name: "rating" },
    { label: t("STATUS"), name: "status" },
    { label: t("OPTIONS"), name: "button" },
  ];

  const listedTeachers = teacherManagement?.map((item) => {
    return {
      id: item.userId,
      teacherId: item.teacherId,
      rating: item.averageRating,
      demoClass: item.demoClass ? "Yes" : "No",
      name: decryptIfEncrypted(item.teacherName),
      firstClass: item.firstClass ? "Yes" : "No",
      country: decryptIfEncrypted(item.countryName),
      status: item.isActive ? "Active" : "Inactive",
      isTraining: item?.teacherData?.isTraining ? "Yes" : "No",
      otherLanguage: item?.languagesOfTeacher?.otherLanguages?.map((item) => item).join(", "),
      nativeLanguage: item?.languagesOfTeacher?.nativeLanguages?.map((item) => item).join(", "),
    };
  });

  const inputChange = (e, name) => {
    const { value } = e.target;
    setFilter((state) => ({ ...state, [name]: value }));
  };

  const applyFilters = () => dispatch(getTeachers(filter));

  const resetFilter = async () => {
    let newFilter = {
      name: "",
      nativeLanguage: "",
      otherLanguage: "",
      country: "",
      firstClass: "",
      demoClass: "",
      ratingFrom: "",
      ratingTo: "",
      isTraining: "",
      status: "",
    };

    setFilter(newFilter);
    dispatch(getTeachers(newFilter));
  };

  const handleNewTeacher = () => {
    dispatch(setDetailTeacher({}));
    navigate("/teachers/new", { newTeacher: true });
  };

  useEffect(() => {
    dispatch(getTeachers(filter));
    dispatch(getCountries()).then((res) => setCountries(res));
    dispatch(getGlobalLanguages()).then((res) => setGlobalLanguages(res));

    // eslint-disable-next-line
  }, []);

  return (
    <div className="view">
      <SideMenu />
      <section>
        <Header
          title={t("ITEM_TEACHERS")}
          icon={<img src={teacherIcon} alt="teacherIcon" />}
          typeIcon="custom"
          rightButton={
            <button className="buttonRight" onClick={handleNewTeacher}>
              <i className="fa fa-plus" aria-hidden="true" />
              {t("ADD_TEACHER")}
            </button>
          }
          t={t}
        />

        <div className="content">
          <Filter filters={filters} inputChange={inputChange} submit={applyFilters} clear={resetFilter} />
          <TableList component="teachers" listItems={listItems} listedItems={listedTeachers} />
        </div>
      </section>
    </div>
  );
}

export default Teachers;
